// ios-simulator-detection.js

/**
 * Detects if the current environment is an iOS simulator and provides fallback options
 * for notifications and other features that might not work in the simulator.
 */

/**
 * Detect if the current environment is an iOS simulator
 * @returns {boolean} True if running in iOS simulator, false otherwise
 */
export const isIOSSimulator = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for standard simulator strings
    if (ua.includes('iPhone Simulator') || ua.includes('iPad Simulator')) {
      return true;
    }
    
    // Additional indicators that might suggest simulator
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isLocalhostOrNonStandard = window.location.hostname === 'localhost' || 
      window.location.hostname.includes('local') ||
      window.location.port === '3000' || 
      window.location.port === '8080';
    
    // In the simulator, the devicePixelRatio often doesn't match standard iOS devices
    // and there are often inconsistencies in the user agent
    const hasNonStandardPixelRatio = isIOS && 
      (window.devicePixelRatio !== 2 && window.devicePixelRatio !== 3);
    
    const missingIOSFeatures = isIOS && 
      (!('standalone' in window.navigator) || 
       !('gesture' in window) || 
       !window.ApplePaySession);
    
    // Local dev environment on iOS = high probability of simulator
    if (isIOS && isLocalhostOrNonStandard && (hasNonStandardPixelRatio || missingIOSFeatures)) {
      return true;
    }
    
    return false;
  };
  
  /**
   * Check if Firebase Cloud Messaging is supported in the current environment
   * @returns {Promise<boolean>} True if FCM is supported, false otherwise
   */
  export const checkFCMSupport = async () => {
    try {
      // Import Firebase dynamically to avoid bundling issues
      const { isSupported } = await import('firebase/messaging');
      return await isSupported();
    } catch (error) {
      console.error('Error checking FCM support:', error);
      return false;
    }
  };
  
  /**
   * Detect iOS version from user agent
   * @returns {Object|null} Object with major and minor version numbers, or null if not iOS
   */
  export const getIOSVersion = () => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    
    if (!isIOS) {
      return null;
    }
    
    const match = ua.match(/OS (\d+)_?(\d+)?/);
    if (match) {
      return {
        major: parseInt(match[1], 10),
        minor: match[2] ? parseInt(match[2], 10) : 0
      };
    }
    
    return null;
  };
  
  /**
   * Checks if the current iOS version supports web push notifications
   * @returns {boolean} True if iOS supports web push, false otherwise
   */
  export const supportsIOSWebPush = () => {
    const version = getIOSVersion();
    
    if (!version) {
      return false; // Not iOS
    }
    
    // iOS 16.4+ supports web push
    return version.major > 16 || (version.major === 16 && version.minor >= 4);
  };
  
  /**
   * Setup fallback notification system for environments where push notifications are not supported
   * @param {Function} notificationCallback Callback function to execute when a fallback notification arrives
   * @returns {Object} Functions to control the fallback system
   */
  export const setupFallbackNotifications = (notificationCallback) => {
    // Using local storage to simulate notifications for development environments
    const NOTIFICATION_QUEUE_KEY = 'fallback_notification_queue';
    
    // Check for pending notifications every 5 seconds
    const pollInterval = setInterval(() => {
      try {
        const queueStr = localStorage.getItem(NOTIFICATION_QUEUE_KEY);
        if (queueStr) {
          const queue = JSON.parse(queueStr);
          if (queue && queue.length > 0) {
            // Process one notification at a time
            const notification = queue.shift();
            localStorage.setItem(NOTIFICATION_QUEUE_KEY, JSON.stringify(queue));
            
            if (notificationCallback && typeof notificationCallback === 'function') {
              notificationCallback(notification);
            }
          }
        }
      } catch (error) {
        console.error('Error processing fallback notifications:', error);
      }
    }, 5000);
    
    // Method to add a notification to the queue (can be called from another tab)
    const sendFallbackNotification = (title, body, data = {}) => {
      try {
        const notification = {
          notification: {
            title,
            body
          },
          data,
          timestamp: new Date().toISOString(),
          id: Date.now()
        };
        
        const queueStr = localStorage.getItem(NOTIFICATION_QUEUE_KEY);
        const queue = queueStr ? JSON.parse(queueStr) : [];
        queue.push(notification);
        localStorage.setItem(NOTIFICATION_QUEUE_KEY, JSON.stringify(queue));
      } catch (error) {
        console.error('Error sending fallback notification:', error);
      }
    };
    
    // Clean up on exit
    const cleanup = () => {
      clearInterval(pollInterval);
    };
    
    return {
      sendFallbackNotification,
      cleanup
    };
  };
  
  /**
   * Creates a test notification using the fallback system
   * Can be used from the browser console for testing
   */
  export const createTestNotification = () => {
    if (isIOSSimulator()) {
      console.log('Creating test notification for iOS simulator');
      const { sendFallbackNotification } = setupFallbackNotifications();
      sendFallbackNotification(
        'Test Notification', 
        'This is a test notification sent at ' + new Date().toLocaleTimeString()
      );
      return true;
    } else {
      console.log('Not in iOS simulator - normal notifications should work');
      return false;
    }
  };
  
  /**
   * Provides a complete compatibility check for notifications
   * @returns {Promise<Object>} Compatibility information
   */
  export const checkNotificationCompatibility = async () => {
    const isSimulator = isIOSSimulator();
    const fcmSupported = await checkFCMSupport();
    const hasNotifications = 'Notification' in window;
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const iosWebPushSupported = supportsIOSWebPush();
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    return {
      isSimulator,
      fcmSupported,
      hasNotifications,
      hasServiceWorker,
      hasPushManager,
      iosWebPushSupported,
      isStandalone,
      shouldUseFallback: isSimulator || !fcmSupported,
      reason: isSimulator 
        ? 'iOS Simulator environment detected' 
        : !fcmSupported 
          ? 'Firebase Cloud Messaging not supported' 
          : !hasNotifications 
            ? 'Notifications API not supported'
            : !hasServiceWorker 
              ? 'Service Worker API not supported'
              : !hasPushManager
                ? 'Push Manager API not supported'
                : null
    };
  };
  
  export default {
    isIOSSimulator,
    checkFCMSupport,
    getIOSVersion,
    supportsIOSWebPush,
    setupFallbackNotifications,
    createTestNotification,
    checkNotificationCompatibility
  };